import type { LeadSignalTimelineDto } from '@/api/leads'
import type { CompanySignalDto } from '@/api/leads'
import type { FileImportRowGridDto } from '@/types/campaignAnalytics'

export interface ExportColumn {
  id: string
  label: string
  getValue: (lead: LeadSignalTimelineDto) => string | number | boolean | null | undefined
}

export interface CompanyExportColumn {
  id: string
  label: string
  getValue: (company: CompanySignalDto) => string | number | boolean | null | undefined
}

export interface AudienceContactExportColumn {
  id: string
  label: string
  getValue: (contact: FileImportRowGridDto) => string | number | boolean | null | undefined
}

// Helper to format date for Excel
function formatDateForExcel(isoDate: string | null | undefined): string {
  if (!isoDate) return ''
  try {
    const date = new Date(isoDate)
    // Excel-friendly format: MM/DD/YYYY HH:MM:SS
    return date.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    })
  } catch {
    return ''
  }
}

// Helper to join array values
function joinArray(arr: string[] | undefined | null): string {
  return arr?.join(', ') || ''
}

// Define all available lead export columns
export const LEAD_EXPORT_COLUMNS: ExportColumn[] = [
  { id: 'firstName', label: 'First Name', getValue: (l) => l.FirstName || '' },
  { id: 'lastName', label: 'Last Name', getValue: (l) => l.LastName || '' },
  { id: 'fullName', label: 'Full Name', getValue: (l) => l.FullName || '' },
  { id: 'email', label: 'Email', getValue: (l) => l.Email || '' },
  { id: 'phone', label: 'Phone', getValue: (l) => l.Phone || l.GlobalContactPhone || '' },
  { id: 'title', label: 'Title', getValue: (l) => l.JobTitle || '' },
  { id: 'company', label: 'Company', getValue: (l) => l.Company || '' },
  { id: 'companyWebsite', label: 'Company Website', getValue: (l) => l.CompanyWebsite || '' },
  { id: 'domain', label: 'Domain', getValue: (l) => l.Domain || '' },
  { id: 'industry', label: 'Industry', getValue: (l) => l.Industry || l.CompanyIndustry || '' },
  { id: 'companySize', label: 'Company Size', getValue: (l) => l.CompanySize || l.CompanyStaffCountRange || '' },
  { id: 'country', label: 'Country', getValue: (l) => l.Country || '' },
  { id: 'enrichmentStatus', label: 'Enrichment Status', getValue: (l) => l.EnrichmentStatus || '' },
  { id: 'enrichedAt', label: 'Enriched At', getValue: (l) => formatDateForExcel(l.EnrichedAt) },
  { id: 'types', label: 'Web Types', getValue: (l) => joinArray(l.WebTypes) },
  { id: 'signalScore', label: 'Signal Score', getValue: (l) => l.SignalScore },
  { id: 'signalLevel', label: 'Signal Level', getValue: (l) => l.SignalLevel || '' },
  { id: 'interactionCount', label: 'Interaction Count', getValue: (l) => l.InteractionCount },
  { id: 'firstInteractionAt', label: 'First Interaction At', getValue: (l) => formatDateForExcel(l.FirstInteractionAt) },
  { id: 'lastInteractionAt', label: 'Last Interaction At', getValue: (l) => formatDateForExcel(l.LastInteractionAt) },
  { id: 'linkedinUrl', label: 'LinkedIn Profile', getValue: (l) => l.LinkedinProfileUrl || '' },
  { id: 'companyLinkedinUrl', label: 'Company LinkedIn', getValue: (l) => l.CompanyLinkedInUrl || '' },
  { id: 'isAudienceTarget', label: 'Is Audience Target', getValue: (l) => l.IsAudienceTarget ? 'Yes' : 'No' },
  { id: 'webinarAttended', label: 'Webinar Attended', getValue: (l) => l.WebinarAttended === true ? 'Yes' : l.WebinarAttended === false ? 'No' : '' },
  { id: 'webinarMinutesWatched', label: 'Minutes Watched', getValue: (l) => l.WebinarMinutesWatched ?? '' },
  { id: 'attributionSource', label: 'Attribution Source', getValue: (l) => l.WebSourceAttributions?.[0]?.Source || '' },
]

/**
 * Quiz Score, Quiz Band and one column per question, for a quiz web's Leads export (ORB-915). None when no lead
 * has completed the quiz. Questions follow the newest completed result: once anyone has answered, the quiz's
 * question IDs can't change, so every result carries the same set.
 */
export function quizExportColumns(leads: LeadSignalTimelineDto[]): ExportColumn[] {
  const newest = leads
    .map(lead => lead.QuizResult)
    .filter(result => result != null)
    .sort((a, b) => (b.CompletedAt ?? '').localeCompare(a.CompletedAt ?? ''))[0]
  if (!newest) return []

  return [
    { id: 'quizScore', label: 'Quiz Score', getValue: (l) => l.QuizResult?.Score ?? '' },
    { id: 'quizBand', label: 'Quiz Band', getValue: (l) => l.QuizResult?.BandName ?? '' },
    ...newest.Answers.map((question): ExportColumn => ({
      id: `quiz_${question.QuestionId}`,
      label: question.QuestionText || question.QuestionId,
      getValue: (l) => {
        if (!l.QuizResult) return ''
        const answer = l.QuizResult.Answers.find(a => a.QuestionId === question.QuestionId)
        if (!answer) return ''
        return answer.Value ?? 'Not applicable'
      },
    })),
  ]
}

// Define company export columns
export const COMPANY_EXPORT_COLUMNS: CompanyExportColumn[] = [
  { id: 'company', label: 'Company', getValue: (c) => c.CompanyName || '' },
  { id: 'companyWebsite', label: 'Company Website', getValue: (c) => c.CompanyWebsite || '' },
  { id: 'companyLinkedinUrl', label: 'Company LinkedIn', getValue: (c) => c.CompanyLinkedInUrl || '' },
  { id: 'industry', label: 'Industry', getValue: (c) => c.Industry || '' },
  { id: 'staffCountRange', label: 'Staff Count Range', getValue: (c) => c.StaffCountRange || '' },
  { id: 'leadCount', label: 'Lead Count', getValue: (c) => c.LeadCount },
  { id: 'signalScore', label: 'Signal Score', getValue: (c) => c.SignalScore },
  { id: 'lastConversionAt', label: 'Last Interaction At', getValue: (c) => formatDateForExcel(c.LastConversionAt) },
]

/**
 * Audience contacts — the rows on a campaign's Contacts tab, which come from the imported file rather
 * than from a conversion. Deliberately the columns a person is worked from, not the whole DTO: the
 * import bookkeeping (FileImportId, RowNumber, attempt counts, LatestError) means nothing in a
 * spreadsheet, and enrichment state is the one piece of processing status that does.
 */
export const AUDIENCE_CONTACT_EXPORT_COLUMNS: AudienceContactExportColumn[] = [
  { id: 'firstName', label: 'First Name', getValue: (c) => c.FirstName || '' },
  { id: 'lastName', label: 'Last Name', getValue: (c) => c.LastName || '' },
  { id: 'email', label: 'Email', getValue: (c) => c.Email || '' },
  { id: 'phone', label: 'Phone', getValue: (c) => c.Phone || c.GlobalContactPhone || '' },
  { id: 'jobTitle', label: 'Job Title', getValue: (c) => c.JobTitle || '' },
  { id: 'company', label: 'Company', getValue: (c) => c.CompanyName || '' },
  { id: 'companyWebsite', label: 'Company Website', getValue: (c) => c.CompanyWebsite || '' },
  { id: 'industry', label: 'Industry', getValue: (c) => c.Industry || '' },
  { id: 'country', label: 'Country', getValue: (c) => c.Country || '' },
  { id: 'linkedin', label: 'LinkedIn', getValue: (c) => c.LinkedinProfileUrl || '' },
  { id: 'enrichmentStatus', label: 'Enrichment Status', getValue: (c) => c.EnrichmentStatus || '' },
  { id: 'enrichedAt', label: 'Enriched At', getValue: (c) => formatDateForExcel(c.EnrichedAt) },
]

/**
 * Convert leads data to CSV format
 */
export function exportLeadsToCSV(
  leads: LeadSignalTimelineDto[],
  columns: ExportColumn[] = LEAD_EXPORT_COLUMNS
): string {
  if (leads.length === 0) return ''

  // Create header row
  const headers = columns.map(col => col.label)

  // Create data rows
  const rows = leads.map(lead =>
    columns.map(col => {
      const value = col.getValue(lead)
      // Escape values containing commas, quotes, or newlines
      if (value === null || value === undefined) return ''
      const stringValue = String(value)
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`
      }
      return stringValue
    })
  )

  // Combine header and rows
  return [headers.join(','), ...rows.map(row => row.join(','))].join('\n')
}

/**
 * Convert companies data to CSV format
 */
export function exportCompaniesToCSV(
  companies: CompanySignalDto[],
  columns: CompanyExportColumn[] = COMPANY_EXPORT_COLUMNS
): string {
  if (companies.length === 0) return ''

  // Create header row
  const headers = columns.map(col => col.label)

  // Create data rows
  const rows = companies.map(company =>
    columns.map(col => {
      const value = col.getValue(company)
      // Escape values containing commas, quotes, or newlines
      if (value === null || value === undefined) return ''
      const stringValue = String(value)
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`
      }
      return stringValue
    })
  )

  // Combine header and rows
  return [headers.join(','), ...rows.map(row => row.join(','))].join('\n')
}

/**
 * Generate export filename with company, workspace, context, and detailed timestamp
 */
export function generateExportFilename(
  companyName: string | undefined,
  workspaceName: string | undefined,
  context: string,
  type: 'leads' | 'companies' | 'contacts'
): string {
  const now = new Date()
  const timestamp = now.toISOString()
    .replace('T', ' ')
    .replace(/\..+/, '')
    .replace(/:/g, '-')

  const parts: string[] = []
  if (companyName) parts.push(companyName)
  if (workspaceName) parts.push(workspaceName)
  const label = type === 'leads' ? 'Leads' : type === 'companies' ? 'Companies' : 'Contacts'
  parts.push(`${context} ${label}`)
  parts.push(timestamp)

  return `${parts.join(' - ')}.csv`
}

/**
 * Trigger browser download of CSV file
 */
export function downloadCSV(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)

  // Create a temporary link element
  const link = document.createElement('a')
  link.href = url
  link.download = filename

  // Prevent preact-iso router from intercepting this
  link.setAttribute('rel', 'noopener noreferrer')
  link.setAttribute('target', '_self')

  // Trigger download without adding to DOM to avoid routing issues
  const clickEvent = new MouseEvent('click', {
    view: window,
    bubbles: false,
    cancelable: true
  })

  link.dispatchEvent(clickEvent)

  // Clean up
  setTimeout(() => {
    URL.revokeObjectURL(url)
  }, 100)
}

/**
 * Export leads to Excel (CSV format)
 */
export function exportLeadsToExcel(
  leads: LeadSignalTimelineDto[],
  filename: string,
  columns: ExportColumn[] = LEAD_EXPORT_COLUMNS
): void {
  const csv = exportLeadsToCSV(leads, columns)
  downloadCSV(csv, filename)
}

/**
 * Export companies to Excel (CSV format)
 */
export function exportCompaniesToExcel(
  companies: CompanySignalDto[],
  filename: string,
  columns: CompanyExportColumn[] = COMPANY_EXPORT_COLUMNS
): void {
  const csv = exportCompaniesToCSV(companies, columns)
  downloadCSV(csv, filename)
}
